import React, { useState, useEffect } from 'react';
import { CaretLeft, CaretRight } from 'phosphor-react';
import testimonials from '../data/testimonials.json';

const AUTO_ADVANCE_MS = 5000;

// Per-instance style overrides — 'light' is the original, default look;
// 'dark' is for placement over a photo/overlay background (e.g. the hero).
const THEMES = {
  light: {
    section: 'bg-transparent',
    quote: 'text-gray-700',
    name: 'text-terrapin-blue',
    company: 'text-gray-500',
    dotInactive: 'bg-gray-300 hover:bg-terrapin-blue',
  },
  dark: {
    section: 'bg-transparent',
    quote: 'text-gray-200',
    name: 'text-gray-100',
    company: 'text-gray-300',
    dotInactive: 'bg-white/40 hover:bg-white/70',
  },
};

const TRANSITION_MS = 300;

const TestimonialCarousel = ({ theme = 'light' }) => {
  const themeClasses = THEMES[theme] || THEMES.light;

  const [current, setCurrent] = useState(0);
  const [previous, setPrevious] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState('forward'); // 'forward' | 'backward'
  const [isEntering, setIsEntering] = useState(false);

  // Auto-advance, paused on hover. Restarts the 5s window on any change
  // (auto or manual) since `current` is a dependency.
  useEffect(() => {
    if (isPaused || testimonials.length <= 1) return;
    const interval = setInterval(() => {
      setDirection('forward');
      setPrevious(current);
      setIsEntering(true);
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(interval);
  }, [isPaused, current]);

  // The incoming slide is a brand-new DOM node (keyed to `current`), so its
  // first paint lands off-canvas; settling it to translate-x-0 on the next
  // frame — outside React's batched update — is what actually animates it.
  // The outgoing slide keeps the DOM node it already had (keyed to
  // `previous`, unchanged across this transition), so it animates straight
  // from its last painted position with no such trick needed.
  useEffect(() => {
    if (!isEntering) return;
    const raf = requestAnimationFrame(() => setIsEntering(false));
    return () => cancelAnimationFrame(raf);
  }, [current, isEntering]);

  // Once the transition has had time to finish, drop the outgoing slide.
  useEffect(() => {
    if (previous === null) return;
    const timer = setTimeout(() => setPrevious(null), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [current, previous]);

  if (testimonials.length === 0) return null;

  const goToPrevious = () => {
    setDirection('backward');
    setPrevious(current);
    setIsEntering(true);
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setDirection('forward');
    setPrevious(current);
    setIsEntering(true);
    setCurrent((prev) => (prev + 1) % testimonials.length);
  };

  const currentTestimonial = testimonials[current];
  const previousTestimonial = previous !== null ? testimonials[previous] : null;

  // Forward travel: incoming enters from the right, outgoing exits left.
  // Backward travel: incoming enters from the left, outgoing exits right.
  const enterFromClass = direction === 'forward' ? 'translate-x-full' : '-translate-x-full';
  const exitToClass = direction === 'forward' ? '-translate-x-full' : 'translate-x-full';
  const incomingClass = isEntering ? enterFromClass : 'translate-x-0';

  const renderSlide = (testimonial, positionClass, slideKey) => (
    <div
      key={slideKey}
      className={`absolute inset-0 flex flex-col items-center justify-center text-center transform transition-transform duration-300 ease-in-out ${positionClass}`}
    >
      <div
        className="w-full flex-shrink-0 overflow-hidden"
        style={{ maxHeight: '84px' }}
      >
        <p
          className={`text-lg sm:text-md italic mb-2 ${themeClasses.quote}`}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          &ldquo;{testimonial.quote}&rdquo;
        </p>
      </div>
      <p className={`flex-shrink-0 font-semibold ${themeClasses.name}`}>{testimonial.name}</p>
      <p className={`flex-shrink-0 text-sm ${themeClasses.company}`}>{testimonial.company}</p>
    </div>
  );

  return (
    <section
      className={`relative rounded-lg py-2 px-6 sm:px-1 w-4/5 mx-auto ${themeClasses.section}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex items-center gap-4 sm:gap-8">
        <button
          type="button"
          onClick={goToPrevious}
          aria-label="Previous testimonial"
          className="btn-base btn-primary rounded-full p-3 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2"
        >
          <CaretLeft size={18} weight="bold" />
        </button>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="relative flex-1 min-w-0 h-48 overflow-hidden">
          {previousTestimonial && renderSlide(previousTestimonial, exitToClass, previous)}
          {renderSlide(currentTestimonial, incomingClass, current)}
        </div>
      </div>


        <button
          type="button"
          onClick={goToNext}
          aria-label="Next testimonial"
          className="btn-base btn-primary rounded-full p-3 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2"
        >
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 mt-1">
        {testimonials.map((t, index) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setCurrent(index)}
            aria-label={`Go to testimonial ${index + 1}`}
            aria-current={index === current}
            className={`h-2.5 w-2.5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2 ${index === current ? 'bg-terrapin-red' : themeClasses.dotInactive
              }`}
          />
        ))}
      </div>
    </section>
  );
};

export default TestimonialCarousel;
